/**
 * Formats an epoch millisecond timestamp for display in the org directory.
 * Output: "Jan 1, 2024" (en-US locale, short month)
 */
export const formatDisplayDate = (date: number): string => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Formats a date-only API value (`YYYY-MM-DD`) as `MM/DD/YYYY`.
 *
 * Two things distinguish it from `formatDisplayDate`: that one takes epoch
 * milliseconds and renders a written month, this one takes the date-only string
 * shape and renders it numerically. A date-only string parses as UTC midnight,
 * so it is read back in UTC — reading it in the viewer's zone would show the day
 * before for anyone west of Greenwich.
 *
 * Both parts are zero-padded so a column of dates lines up.
 *
 * An unparseable value comes back as it was given rather than as "Invalid Date",
 * so a malformed record shows what is actually stored.
 */
export const formatIsoDateNumeric = (date: string): string => {
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) {
    return date
  }
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  })
}
